/** Shared room chrome: header, invite dialog, confirm dialog, scene pill and the notice toast hook. */
import { useCallback, useEffect, type ReactNode } from 'react';
import { useI18n } from '../i18n';
import { LanguageSwitcher } from '../i18n';
import { ThemeToggle } from '../theme';
import { Modal, useSessionState } from '../ui';
import { sceneNameKey, type SceneId } from '../scenarios/scenes';
import type { MeetingSession } from '../meeting/session';

export function ScenePill({ scene }: { scene: SceneId }) {
  const { t } = useI18n();
  return (
    <span className={`nm-scene-pill is-${scene}`} data-testid="room-scene" data-scene={scene}>
      {t(sceneNameKey(scene))}
    </span>
  );
}

export function RoomHeader({
  scene,
  channel,
  meta,
  actions,
  onLeave,
}: {
  scene: SceneId;
  channel: string;
  meta?: ReactNode;
  actions?: ReactNode;
  onLeave: () => void;
}) {
  const { t } = useI18n();
  return (
    <header className="nm-room-head" data-testid="room-header">
      <div className="nm-room-head__title">
        <ScenePill scene={scene} />
        <strong data-testid="room-channel">{channel}</strong>
        <span className="nm-sub">{t('app.tagline')}</span>
      </div>
      <div className="nm-room-head__meta" data-testid="room-toolbar">
        {meta}
        {actions}
        <LanguageSwitcher />
        <ThemeToggle />
        <button className="nm-btn" onClick={onLeave} data-testid="room-leave">
          {t('room.leave')}
        </button>
      </div>
    </header>
  );
}

export function InviteModal({
  title,
  link,
  note,
  count,
  channel,
  number,
  onCopy,
  onClose,
  testId = 'invite-dialog',
}: {
  title: string;
  link: string;
  note: string;
  count?: number;
  channel?: string | null;
  number?: string | null;
  onCopy: () => void;
  onClose: () => void;
  testId?: string;
}) {
  const { t } = useI18n();
  return (
    <Modal
      title={title}
      onClose={onClose}
      testId={testId}
      actions={
        <>
          <button className="nm-btn" onClick={onCopy} data-testid={`${testId}-copy`}>
            {t('meeting.invite.copy')}
          </button>
          <button className="nm-btn" onClick={onClose}>
            {t('common.close')}
          </button>
        </>
      }
    >
      <div className="nm-field">
        <span>{t('meeting.invite.link')}</span>
        <input
          className="nm-input"
          readOnly
          value={link}
          data-testid={`${testId}-link`}
          onFocus={(event) => event.currentTarget.select()}
        />
      </div>
      <div className="nm-kv">
        {channel ? (
          <>
            <dt>{t('meeting.invite.number')}</dt>
            <dd>{channel}</dd>
          </>
        ) : null}
        {number ? (
          <>
            <dt>{t('meeting.settings.mode')}</dt>
            <dd>{number}</dd>
          </>
        ) : null}
        <dt>{t('meeting.invite.members')}</dt>
        <dd>
          {typeof count === 'number'
            ? t('meeting.invite.membersCount', { count })
            : t('common.unknown')}
        </dd>
      </div>
      <p className="nm-sub">{note}</p>
    </Modal>
  );
}

export function ConfirmModal({
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
  testId,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
  testId?: string;
}) {
  const { t } = useI18n();
  return (
    <Modal
      title={title}
      onClose={onClose}
      testId={testId}
      actions={
        <>
          <button className="nm-btn" onClick={onClose}>
            {t('room.stay')}
          </button>
          <button
            className="nm-btn is-danger"
            onClick={onConfirm}
            data-testid={testId ? `${testId}-confirm` : undefined}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p>{body}</p>
    </Modal>
  );
}

/** Show `noticeKey` (host muted you, your mic was approved, ...) once as a toast. */
export function useNoticeToast(session: MeetingSession, show: (message: string) => void): void {
  const state = useSessionState(
    useCallback((listener: () => void) => session.subscribe(listener), [session]),
    useCallback(() => session.getState(), [session]),
  );
  const { t } = useI18n();
  const notice = state.noticeKey;
  useEffect(() => {
    if (!notice) return;
    const key = session.consumeNotice();
    if (key) show(t(key));
  }, [notice, session, show, t]);
}
